<template>
  <div class="h-full overflow-x-auto">
    <loader :is-loading="loading" />
    <lf-table
      v-model:pin-enabled="pinEnabled"
      :columns="columns"
      :data="data"
      :loading="loading"
      :select-all="select"
      :class="['scorecard-list-table', tableHeightClass]"
      :checked-rows="selectedIds"
      row-is-clickable
      pin-option
      header-sticky
      :freeze-first-two-columns-when-pinned="isWorkflowBuilder"
      @row-clicked="editScorecard"
      @selected-rows="handleSelectAll"
      @selected-row="handleSelectRow"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import compact from "lodash/compact";

import {
  SCORECARD_STATUS_DRAFT,
  SCORECARD_STATUS_FINISHED,
  SCORECARD_TYPE_MANUAL,
} from "@/helpers/constants";
import { formatUserFullName } from "@/helpers/formatting";
import { scorecardResultsRunAtFormatter } from "@/helpers/scorecards";
import {
  ScorecardsIsBacktestReport,
  ScorecardsIsViewKey,
  ScorecardsIsWorkflowBuilder,
} from "@/helpers/symbols";
import { useLocalStorageSetting } from "@/hooks/options";
import { useScorecards } from "@/hooks/scorecards";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";

import ScorecardActionMenu from "@/components/scorecards/ScorecardActionMenu.vue";
import ScorecardFavoriteButton from "@/components/scorecards/ScorecardFavoriteButton.vue";
import ScorecardId from "@/components/scorecards/tableComponents/ScorecardId.vue";
import ScorecardKnockout from "@/components/scorecards/tableComponents/ScorecardKnockout.vue";
import ScorecardNameRunAndEdit from "@/components/scorecards/tableComponents/ScorecardNameRunAndEdit.vue";
import ScorecardPoints from "@/components/scorecards/tableComponents/ScorecardPoints.vue";
import ScorecardPointsAndResultsBar from "@/components/scorecards/tableComponents/ScorecardPointsAndResultsBar.vue";
import ScorecardRiskTier from "@/components/scorecards/tableComponents/ScorecardRiskTier.vue";
import ScorecardStatus from "@/components/scorecards/tableComponents/ScorecardStatus.vue";
import LfTable from "@/components/ui/table/LfTable.vue";
import DateAndTimeDisplay from "@/components/ui/text/DateAndTimeDisplay.vue";
import LfTag from "@/components/ui/text/LfTag.vue";
import LfTooltipable from "@/components/ui/text/LfTooltipable.vue";

import type { IDealsColumn } from "@/models/applications";
import type { IScorecard } from "@/models/scorecards";
import type { PropType } from "vue";

const props = defineProps({
  data: {
    type: Array as PropType<IScorecard[]>,
    required: true,
  },
  selectedIds: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  select: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (eventName: "update:selectedIds", newSelectedIds: string[]): void;
}>();

const { canUseScorecardActions } = useScorecards(false);
const router = useRouter();
const { canEditWorkflowTemplate } = useActiveWorkflowTemplate();
const { t } = useI18n();
const pinEnabled = useLocalStorageSetting(
  "scorecardListTablePinEnabled",
  false
);

const pageIsView = inject(ScorecardsIsViewKey, true);
const isWorkflowBuilder = inject(ScorecardsIsWorkflowBuilder, false);
const isBacktestReport = inject(ScorecardsIsBacktestReport, false);

const tableHeightClass = computed(() =>
  pageIsView ? "h-[75vh] 2xl:h-[80vh]" : "h-[55vh] 2xl:h-[65vh]"
);

const isViewOrWorkflowBuilder = pageIsView || isWorkflowBuilder;
const isWorkflowLive = !isViewOrWorkflowBuilder;

const columns = computed(() =>
  compact<IDealsColumn>([
    {
      label: t("COMMON.TEMPLATE_NAME"),
      key: "name",
      columnClass: pageIsView ? "w-500px" : "w-300px",
      component: ScorecardNameRunAndEdit,
      props: {
        pageIsView,
        isWorkflowLive,
      },
    },
    isViewOrWorkflowBuilder
      ? {
          label: t("COMMON.DESCRIPTION"),
          key: "description",
          component: LfTooltipable,
          props: {
            "text-class": "max-w-40",
          },
        }
      : null,
    {
      label: t("COMMON.TEMPLATE_ID"),
      key: "id",
      component: ScorecardId,
    },
    !isViewOrWorkflowBuilder
      ? {
          label: t("SCORECARDS.KNOCKOUT"),
          key: "knockout",
          component: ScorecardKnockout,
        }
      : null,
    !isViewOrWorkflowBuilder
      ? {
          label: t("SCORECARDS.STATUS"),
          key: "sc_status",
          component: ScorecardStatus,
        }
      : null,
    !isViewOrWorkflowBuilder
      ? {
          label: t("SCORECARDS.RISK_TIER"),
          key: "results",
          component: ScorecardRiskTier,
          removeComponentIfEmpty: true,
          formatter: () => "",
        }
      : null,
    !isWorkflowLive
      ? {
          label: t("COMMON.STATUS"),
          key: "status",
          component: LfTag,
          props: {
            class: "uppercase",
          },
          formatter: getStatusText,
        }
      : null,
    isWorkflowLive && {
      label: t("SCORECARDS.RUN_AT"),
      key: "results",
      component: DateAndTimeDisplay,
      formatter: scorecardResultsRunAtFormatter,
    },
    !isWorkflowBuilder && !pageIsView
      ? {
          label: "",
          key: "points-bar",
          component: ScorecardPointsAndResultsBar,
          columnClass: "min-w-[200px] points-bar",
          props: {
            pageIsView,
            isWorkflowLive,
          },
        }
      : null,
    {
      label: t("SCORECARDS.PTS"),
      key: "points",
      component: ScorecardPoints,
      props: { pageIsView, isWorkflowBuilder },
    },
    !isWorkflowLive && !isBacktestReport
      ? {
          label: t("COMMON.UPDATED_BY"),
          key: "latest_activity.updated_by",
          formatter: formatUserFullName,
        }
      : null,
    !isWorkflowLive && !isBacktestReport
      ? {
          label: t("COMMON.UPDATED_AT"),
          key: "latest_activity.updated_at",
          component: DateAndTimeDisplay,
        }
      : null,
    showEditMenu.value
      ? { label: "", key: "actions", component: ScorecardActionMenu }
      : null,
    isWorkflowBuilder || isWorkflowLive
      ? {
          label: "",
          key: "favorite",
          component: ScorecardFavoriteButton,
          columnClass: "w-[90px]",
        }
      : null,
  ])
);

const showEditMenu = computed(
  () =>
    !isWorkflowBuilder &&
    !isWorkflowLive &&
    !isBacktestReport &&
    pageIsView &&
    canUseScorecardActions.value
);

const editScorecard = (id: string) => {
  if (isWorkflowBuilder && !canEditWorkflowTemplate.value) {
    return;
  }
  const scorecard = props.data.find((sc) => sc.id === id);
  if (!scorecard) {
    return;
  }
  if (pageIsView && !props.select && !isBacktestReport) {
    if (scorecard.type === SCORECARD_TYPE_MANUAL) {
      router.push({ name: "ScorecardsTemplate", params: { id } });
    }
    const routeName =
      scorecard.status === SCORECARD_STATUS_DRAFT
        ? "ScorecardsBuilder"
        : "ScorecardsOverview";

    router.push({ name: routeName, params: { id } });
    return;
  }
  if (isBacktestReport) {
    emit("update:selectedIds", [id]);
  }
};

const handleSelectAll = (rows: Array<string>, checked?: boolean) => {
  if (typeof checked === "undefined") {
    return;
  }
  emit("update:selectedIds", rows);
};

const handleSelectRow = (id: string | number, checked: boolean) => {
  const stringId = String(id);
  const filtered = props.selectedIds.filter(
    (selectedId) => selectedId !== stringId
  );
  const payload = checked ? [...filtered, stringId] : filtered;
  emit("update:selectedIds", payload);
};

const getStatusText = (id: number) =>
  id === SCORECARD_STATUS_FINISHED ? t("COMMON.PUBLISHED") : t("COMMON.DRAFT");
</script>
